import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

// Определение типов для темы
interface ThemeColors {
    primary: {
        main: string;
        light: string;
        dark: string;
    };
    secondary: {
        main: string;
    };
    background: {
        default: string;
        paper: string;
    };
    text: {
        primary: string;
        secondary: string;
    };
}

// Интерфейс контекста темы
interface ThemeContextType {
    colors: ThemeColors;
    isDarkMode: boolean;
    toggleDarkMode: () => void;
}

// Тема по умолчанию (светлая)
const lightTheme: ThemeColors = {
    primary: {
        main: '#5ebeb3',
        light: '#8eefe4',
        dark: '#328f86',
    },
    secondary: {
        main: '#dc004e',
    },
    background: {
        default: '#f5f5f5',
        paper: '#ffffff',
    },
    text: {
        primary: 'rgba(0, 0, 0, 0.87)',
        secondary: 'rgba(0, 0, 0, 0.6)',
    },
};

// Темная тема
const darkTheme: ThemeColors = {
    primary: {
        main: '#90caf9',
        light: '#a6d4fa',
        dark: '#648dae',
    },
    secondary: {
        main: '#f48fb1',
    },
    background: {
        default: '#121212',
        paper: '#1e1e1e',
    },
    text: {
        primary: '#ffffff',
        secondary: 'rgba(255, 255, 255, 0.7)',
    },
};

// Создание контекста с дефолтными значениями
const ThemeContext = createContext<ThemeContextType>({
    colors: lightTheme,
    isDarkMode: false,
    toggleDarkMode: () => { },
});

// Хук для использования контекста темы
export const useTheme = () => useContext(ThemeContext);

// Свойства провайдера темы
interface ThemeProviderProps {
    children: ReactNode;
}

/**
 * Функция для установки CSS переменных темы
 */
const updateCssVariables = (theme: ThemeColors) => {
    const root = document.documentElement;

    // Основные цвета
    root.style.setProperty('--primary-main', theme.primary.main);
    root.style.setProperty('--primary-light', theme.primary.light);
    root.style.setProperty('--primary-dark', theme.primary.dark);
    root.style.setProperty('--secondary-main', theme.secondary.main);

    // Фон
    root.style.setProperty('--background-default', theme.background.default);
    root.style.setProperty('--background-paper', theme.background.paper);

    // Текст
    root.style.setProperty('--text-primary', theme.text.primary);
    root.style.setProperty('--text-secondary', theme.text.secondary);

    // Установка фона для body
    document.body.style.backgroundColor = theme.background.default;
};

// Провайдер темы
export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [colors, setColors] = useState<ThemeColors>(lightTheme);

    // Функция переключения темы
    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode);
        setColors(!isDarkMode ? darkTheme : lightTheme);
    };

    // Обновление CSS переменных при изменении темы
    useEffect(() => {
        updateCssVariables(colors);
    }, [colors]);

    // Инициализируем CSS переменные при первом рендере
    useEffect(() => {
        updateCssVariables(colors);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ThemeContext.Provider value={{ colors, isDarkMode, toggleDarkMode }}>
            {children}
        </ThemeContext.Provider>
    );
};

export default CustomThemeProvider; 